// Hub LoRa: el equipo del casco con WiFi por el que llegan los datos de las
// estaciones del campo. Se vincula a la cuenta con el código de su etiqueta.
export interface Hub {
  id: string;
  siteId: string | null;
  siteName?: string | null;
  status: 'online' | 'offline';
  lastSeenAt: string | null;
  stationIds: string[];
  deviceCount?: number;
  claimedAt: string | null;
  wifiRssi: number | null;
  firmware: string | null;
}

// Órdenes que el dueño le puede mandar al hub (va en una caja cerrada).
export type HubCommand = 'emparejar' | 'reiniciar' | 'wifi';

// Cuánto escucha el hub estaciones nuevas al pedir "Agregar estación".
export const PAIRING_SECONDS = 120;

// Calidad del WiFi del casco según el RSSI que informa el hub.
export function wifiQuality(rssi: number | null): string | null {
  if (rssi === null) return null;
  if (rssi >= -60) return 'buena';
  if (rssi >= -72) return 'regular';
  return 'débil';
}

export interface ClaimedHub {
  hub: Hub;
  // Estaciones del hub que se agregaron a la cuenta al vincularlo.
  createdStations: string[];
}

const HUB_ID = /^hub-[0-9a-f]{6}$/;

// El QR "Vincular" de la etiqueta es una URL de la app
// (…/app/hubs/vincular?hub=hub-a73f38&codigo=K7Q2-M9XA). Escaneado desde el
// lector de la app, lleva a la misma pantalla; null = no es de un hub.
export function hubClaimPathForScan(decodedText: string): string | null {
  try {
    const url = new URL(decodedText);
    const hub = url.searchParams.get('hub');
    const code = url.searchParams.get('codigo');
    if (hub && HUB_ID.test(hub) && code) {
      return `/app/hubs/vincular?hub=${encodeURIComponent(hub)}&codigo=${encodeURIComponent(code)}`;
    }
  } catch {
    // No era una URL.
  }
  return null;
}

// Backoffice: el hub con su dueño y su campo.
export interface AdminHub {
  id: string;
  userId: string | null;
  ownerEmail: string | null;
  siteName: string | null;
  status: 'online' | 'offline';
  lastSeenAt: string | null;
  lastInfo: HubInfo | null;
  stationIds: string[];
  deviceCount: number;
  createdAt: string;
  claimedAt: string | null;
}

// Lo que el hub cuenta de sí mismo en <hub>/hub/info.
export interface HubInfo {
  // Sin batería conectada no viene.
  bateriaMv?: number;
  bateriaPct?: number;
  // Deducido de la tendencia de la tensión (sube, o está llena y enchufada).
  cargando?: boolean;
  wifiRssi?: number;
  uptimeS?: number;
  freeHeapKb?: number;
  // Índice del motivo del último reinicio (mismo orden que la trama de las estaciones).
  resetReason?: number;
  estaciones?: number;
  online?: number;
  fw?: string;
}

export interface HubHealthPoint {
  at: string;
  wifiRssi: number | null;
  freeHeapKb: number | null;
  stationsOnline: number | null;
}

export interface AdminHubDetail {
  hub: AdminHub;
  hourly: HubHealthPoint[];
  restarts7d: number;
  stations: { id: string; name: string; status: 'online' | 'offline'; lastSeenAt: string | null }[];
}

export interface IssuedClaimCode {
  hubId: string;
  claimCode: string;
  claimUrl: string;
}

// Actualización remota: cómo le fue a cada hub con una versión.
export type HubUpdateStatus = 'enviada' | 'descargando' | 'instalada' | 'confirmada' | 'error' | 'revertida' | 'reemplazada';

export interface FirmwareRelease {
  id: string;
  version: string;
  size: number;
  sha256: string;
  notes: string | null;
  createdAt: string;
  updates: { hubId: string; status: HubUpdateStatus; detail: string | null; updatedAt: string }[];
}

export const UPDATE_STATUS_LABEL: Record<HubUpdateStatus, { text: string; tone: 'medium' | 'warning' | 'success' | 'danger' }> = {
  enviada: { text: 'Orden enviada', tone: 'medium' },
  descargando: { text: 'Descargando', tone: 'warning' },
  instalada: { text: 'Instalada, reiniciando', tone: 'warning' },
  confirmada: { text: 'Actualizado', tone: 'success' },
  error: { text: 'Error', tone: 'danger' },
  revertida: { text: 'Volvió a la anterior', tone: 'danger' },
  reemplazada: { text: 'Reemplazada', tone: 'medium' },
};
